import { useQuery } from '@tanstack/react-query';
import { checkEnableCompanySupport } from 'utils/helpers/featureFlags';

import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import {
  getGoalsInsightsGroupByIndividualData,
  getGoalsInsightsGroupByIndividualOldData,
  getGoalsInsightsGroupByGoalsData,
} from 'services/analytics/goal';

import { getDepartmentChildFunctionsIdsApi, getDepartmentHierarchy } from '../api';
import { getGoalsCascadedView } from '../services';

const getData = async ({
  groupByKeys,
  splitByKeys,
  orderBy,
  timeBounds,
  attributeFilter,
  cohort,
  isHierarchyView,
  pageType,
  isTreeView,
  mergeColumns,
  ownership,
}) => {
  try {
    const res = await getGoalsCascadedView({
      groupByKeys,
      splitByKeys,
      orderBy,
      timeBounds,
      attributeFilter,
      cohort,
      isHierarchyView,
      pageType,
      isTreeView,
      mergeColumns,
      ownership,
    });

    let formattedRes = res;

    if (checkEnableCompanySupport()) {
      formattedRes = formattedRes?.map((row) => {
        const newRow = { ...row };
        if (
          !newRow.companies ||
          newRow.companies.length === 0 ||
          newRow.companies.filter((company) => company?.companyName).length === 0
        ) {
          newRow.companies = [{ id: 'NA', companyName: 'NA' }];
        }
        if (
          !newRow.empLocations ||
          newRow.empLocations.length === 0 ||
          newRow.empLocations.filter((loc) => loc).length === 0
        ) {
          newRow.empLocations = ['NA'];
        }
        return newRow;
      });
    }
    return formattedRes;
  } catch (err) {
    console.error('Error fetching goals cascaded view data : ', err);
  }
};

export const goalsCascadedViewReactQueryKey = ({
  cohort,
  groupByKeys,
  splitByKeys,
  orderBy,
  timeBounds,
  attributeFilter,
  isHierarchyView,
  pageType,
  ownership,
}) => [
  REACT_QUERY_KEYS.GoalsCascadedData,
  cohort?.id,
  groupByKeys?.keyName,
  splitByKeys?.keyName,
  orderBy,
  timeBounds,
  attributeFilter,
  isHierarchyView,
  pageType,
  ownership,
];

export const useGoalsCascadedViewData = ({
  enabled = true,
  groupByKeys,
  splitByKeys,
  orderBy,
  timeBounds,
  attributeFilter,
  cohort,
  isHierarchyView,
  pageType = 'TEAM_HUB',
  isTreeView = false,
  mergeColumns = false,
  ownership,
}) =>
  useQuery(
    goalsCascadedViewReactQueryKey({
      cohort,
      groupByKeys,
      splitByKeys,
      orderBy,
      timeBounds,
      attributeFilter,
      isHierarchyView,
      pageType,
      isTreeView,
      mergeColumns,
      ownership,
    }),
    () =>
      getData({
        groupByKeys,
        splitByKeys,
        orderBy,
        timeBounds,
        attributeFilter,
        cohort,
        isHierarchyView,
        pageType,
        isTreeView,
        mergeColumns,
        ownership,
      }),
    { enabled: enabled && !!timeBounds[0] }
  );

export const goalsInsightsGroupByIndividualDataQueryKey = ({
  cohort,
  groupByKeys,
  splitByKeys,
  orderBy,
  timeBounds,
}) => [
  REACT_QUERY_KEYS.GoalsInsightsGroupByIndividualData,
  cohort?.id,
  groupByKeys?.keyName,
  splitByKeys?.keyName,
  orderBy,
  timeBounds,
];

export const useGoalsInsightsGroupByIndividualData = ({ groupByKeys, splitByKeys, orderBy, timeBounds, cohort }) =>
  useQuery(
    goalsInsightsGroupByIndividualDataQueryKey({
      cohort,
      groupByKeys,
      splitByKeys,
      orderBy,
      timeBounds,
    }),
    () =>
      getGoalsInsightsGroupByIndividualData({
        selectedCohort: cohort,
        groupByKeys,
        splitByKeys,
        orderBy,
        timeBounds,
      }),
    { enabled: !!timeBounds?.[0] }
  );

/**
 * @deprecated 06-08-2024
 */
export const useGoalsInsightsGroupByIndividualDataOld = ({ groupByKeys, splitByKeys, orderBy, timeBounds, cohort }) =>
  useQuery(
    goalsInsightsGroupByIndividualDataQueryKey({
      cohort,
      groupByKeys,
      splitByKeys,
      orderBy,
      timeBounds,
    }),
    () =>
      getGoalsInsightsGroupByIndividualOldData({
        selectedCohort: cohort,
        groupByKeys,
        splitByKeys,
        orderBy,
        timeBounds,
      }),
    { enabled: !!timeBounds[0] }
  );

export const useDepartmentChildFunctionsIds = ({ enabled = true, functionId }) =>
  useQuery(
    [REACT_QUERY_KEYS.DepartmentChildFunctionsIds, functionId],
    () => getDepartmentChildFunctionsIdsApi({ functionId }).then((res) => res.entity),
    { enabled: enabled && Boolean(functionId) }
  );

export const useFetchDepartmentHierarchy = () =>
  useQuery([REACT_QUERY_KEYS.DepartmentHierarchy], () => getDepartmentHierarchy().then((res) => res.entity));

export const goalsInsightsGroupByGoalsDataQueryKey = ({
  cohort,
  groupByKeys,
  splitByKeys,
  orderBy,
  timeBounds,
  attributeFilter,
  isHierarchyView,
  pageType,
}) => [
  REACT_QUERY_KEYS.GoalsInsightsGroupByGoalsData,
  cohort?.id,
  groupByKeys?.keyName,
  splitByKeys?.keyName,
  orderBy,
  timeBounds,
  attributeFilter,
  isHierarchyView,
  pageType,
];

export const useGoalsInsightsGroupByGoalsQuery = ({
  enabled = true,
  groupByKeys,
  splitByKeys,
  orderBy,
  timeBounds,
  attributeFilter,
  cohort,
  isHierarchyView,
  pageType = 'TEAM_HUB',
  isTreeView = false,
}) =>
  useQuery(
    goalsInsightsGroupByGoalsDataQueryKey({
      cohort,
      groupByKeys,
      splitByKeys,
      orderBy,
      timeBounds,
      attributeFilter,
      isHierarchyView,
      pageType,
      isTreeView,
    }),
    () =>
      getGoalsInsightsGroupByGoalsData({
        groupByKeys,
        splitByKeys,
        orderBy,
        timeBounds,
        attributeFilter,
        cohort,
        isHierarchyView,
        pageType,
        isTreeView,
      }),
    { enabled: enabled && !!timeBounds[0] }
  );
